import { ROOT_NODE, useEditor, useNode } from "@craftjs/core";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Actions } from "src/types";

import { ButtonColorPalete } from "@/components/editor/themes/theme-color-palette/theme-color-palette";
import { Input, Select } from "@/components/shared/forms";

import SettingsTitleWrapper from "../../shared/settings-title-wrapper";

export default function ButtonSettings() {
  const { topLevelBlocks } = useEditor((state) => {
    const root = state.nodes[ROOT_NODE];
    const topLevelBlocks = root.data.nodes
      .map((nodeId) => {
        const node = state.nodes[nodeId];
        return node;
      })
      .filter(Boolean);
    return { topLevelBlocks };
  });

  const {
    props,
    actions: { setProp },
  } = useNode((node) => ({
    props: node.data.props,
  }));
  const { t } = useTranslation();

  const [localActionKind, setLocalActionKind] = useState(props.action.kind);

  const options = useMemo(
    () => [
      { label: t("preference-panel.none"), value: Actions.None },
      {
        label: t("preference-panel.ticket-selector"),
        value: Actions.TicketSelector,
      },
      {
        label: t("preference-panel.section-of-page"),
        value: Actions.SectionOfPage,
      },
      {
        label: t("preference-panel.external-link"),
        value: Actions.ExternalLink,
      },
      { label: t("preference-panel.top-of-page"), value: Actions.TopOfPage },
      {
        label: t("preference-panel.bottom-of-page"),
        value: Actions.BottomOfPage,
      },
    ],
    [t]
  );

  const handleSelect = useCallback(
    (value) => {
      setLocalActionKind(value);
      if (
        // Immediately set the type for these actions as they do not require any additional attributes.
        [
          Actions.None,
          Actions.TicketSelector,
          Actions.TopOfPage,
          Actions.BottomOfPage,
        ].includes(value)
      ) {
        setProp(
          (props) =>
            (props.action = {
              kind: value,
            })
        );
      }
    },
    [setProp]
  );

  const sectionOfPageOptions = useMemo(() => {
    return topLevelBlocks.reduce((sections, block) => {
      sections.push({
        label: t(
          "preference-panel.block-display-name." +
            block.node.displayName.toLowerCase()
        ),
        value: block.id,
      });

      return sections;
    }, []);
  }, [t, topLevelBlocks]);

  return (
    <div className="grid gap-4">
      <SettingsTitleWrapper
        title={props.isLink ? t("common.link") : t("preference-panel.button")}
      >
        <Input
          label={t("preference-panel.display-text")}
          name={t("preference-panel.display-text")}
          value={props.text}
          onChange={(event) => {
            setProp((props) => (props.text = event.currentTarget.value));
          }}
          data-testid="action-settings-component"
        />
      </SettingsTitleWrapper>

      <Select
        dataTestId="action-select"
        name="action"
        label={t("preference-panel.link-to")}
        options={options}
        value={localActionKind || props.action?.kind || Actions.None}
        onChange={handleSelect}
      />

      {localActionKind === Actions.SectionOfPage && (
        <Select
          name="sectionOfPage"
          placeholder={t("preference-panel.select-an-option")}
          label={t("preference-panel.section-of-page")}
          options={sectionOfPageOptions}
          value={props.action.nodeId}
          onChange={(value) => {
            setProp(
              (props) =>
                (props.action = {
                  kind: Actions.SectionOfPage,
                  nodeId: value,
                })
            );
          }}
        />
      )}

      {localActionKind === Actions.ExternalLink && (
        <Input
          data-testid="external-link-input"
          label=""
          name={t("preference-panel.link-to")}
          placeholder="https://www..."
          value={props.action.href}
          onChange={(event) => {
            setProp(
              (props) =>
                (props.action = {
                  kind: Actions.ExternalLink,
                  href: event.currentTarget.value,
                })
            );
          }}
          className="mb-4"
        />
      )}
      {!props.noCustomization && (
        <ButtonColorPalete
          colorSet={props.colorSet}
          onColorChange={(colors) =>
            setProp((props) => (props.colorSet = colors))
          }
        />
      )}
    </div>
  );
}
